import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Fab,
  Paper,
  Typography,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageContainer from "../components/PageContainer";
import PageSection from "../components/PageSection";
import CreateCircleForm, {
  CREATE_CIRCLE_FORM_ID,
  type CreateCircleFormFooterState,
} from "../components/Circles/CreateCircleForm";
import EditCircleForm, {
  EDIT_CIRCLE_FORM_ID,
  type EditCircleFormFooterState,
} from "../components/Circles/EditCircleForm";
import {
  INITIAL_CREATE_CIRCLE_FORM_FOOTER,
  INITIAL_CIRCLE_FORM_FOOTER,
} from "../components/Circles/circleFormFooterState";
import { ManageCircleUsers } from "../components/Circles/ManageCircleUsers";
import CirclesSkeleton from "../components/Circles/CirclesSkeleton";
import SortableCirclesList from "../components/Circles/SortableCirclesList";
import {
  ActionModal,
  DeleteModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
  SearchField,
  EmptyState,
} from "../components/shared";
import { lightTheme } from "../styles/theme";
import { useCircles } from "../hooks/useCircles";
import { api } from "../services/api";
import { useRegisterPageActions } from "../context/PageActionsContext";
import VideoPlayerModal from "../components/VideoPlayerModal";
import { alpha } from "@mui/material/styles";
import { MANAGEMENT_CARD_HEADER_FONT_SIZE } from "../styles/managementCardTypography";
import {
  MANAGEMENT_ADD_MORE_BUTTON_SX,
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../styles/managementShared";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";
import { useSnackbar } from "../context/SnackbarContext";

interface Circle {
  circleId: string;
  name: string; // This becomes schoolName in schools-core
  description: string;
  isPublic: boolean; // Based on public/private setting
  learningPoints?: string[];
  memberCount: number;
  createdAt: string;
  createdBy: string;
  isSubCircle: boolean;
  parentCircle?: string; // Parent circle name if isSubCircle is true
  circleApproved: boolean; // Always true for sub-circles
  logo: string; // Uses main organization logo
  type: string; // Matches parent type
  orgBanner?: string;
  displayOrder?: number;
}

const Circles: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileFabVisible = useMobileFabVisibility(isMobile);
  const mobileFabSx = useMemo(
    () => mobileFabWithScrollVisibility(MANAGEMENT_MOBILE_FAB_SX, mobileFabVisible),
    [mobileFabVisible],
  );
  const styledTheme = lightTheme;
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [circleToEdit, setCircleToEdit] = useState<Circle | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [circleToDelete, setCircleToDelete] = useState<Circle | null>(null);
  const [manageUsersModalOpen, setManageUsersModalOpen] = useState(false);
  const [circleForManagingUsers] = useState<Circle | null>(null);

  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);

  const [createCircleFooter, setCreateCircleFooter] =
    useState<CreateCircleFormFooterState>(INITIAL_CREATE_CIRCLE_FORM_FOOTER);

  const handleCreateCircleFooterState = useCallback(
    (state: CreateCircleFormFooterState) => {
      setCreateCircleFooter(state);
    },
    []
  );

  useEffect(() => {
    if (createModalOpen) {
      setCreateCircleFooter(INITIAL_CREATE_CIRCLE_FORM_FOOTER);
    }
  }, [createModalOpen]);

  const [editCircleFooter, setEditCircleFooter] =
    useState<EditCircleFormFooterState>(INITIAL_CIRCLE_FORM_FOOTER);

  const handleEditCircleFooterState = useCallback(
    (state: EditCircleFormFooterState) => {
      setEditCircleFooter(state);
    },
    []
  );

  useEffect(() => {
    if (editModalOpen) {
      setEditCircleFooter(INITIAL_CIRCLE_FORM_FOOTER);
    }
  }, [editModalOpen]);

  // Use the cached circles hook
  const {
    data: allCircles = [],
    isLoading,
    error,
    refetch: loadCircles,
  } = useCircles();

  // Filter circles based on team member's access level and circleIds
  const circles = useMemo(() => {
    // If user is not a team member (org admin), show all circles
    if (!user?.userType || user.userType !== "team") {
      return allCircles;
    }

    // If user is Admin (case-insensitive check), show all circles
    const accessLevel = user.accessLevel?.toLowerCase();
    if (accessLevel === "admin") {
      return allCircles;
    }

    // For Owner and Creator, only show circles they have access to
    // Check if circleIds exists and is a non-empty array
    const userCircleIds = user.circleIds;
    if (
      userCircleIds &&
      Array.isArray(userCircleIds) &&
      userCircleIds.length > 0
    ) {
      // Filter circles that match the user's circleIds
      const filtered = allCircles.filter((circle) =>
        userCircleIds.includes(circle.circleId)
      );
      return filtered;
    }

    // If no circleIds or empty array, return empty array
    // This means the user has no assigned circles
    return [];
  }, [allCircles, user?.userType, user?.accessLevel, user?.circleIds]);

  const handleCreateCircle = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  // Filter circles based on search query
  const filteredCircles = useMemo(() => {
    if (!searchQuery.trim()) return circles;

    const query = searchQuery.toLowerCase();
    return circles.filter(
      (circle) =>
        circle.name.toLowerCase().includes(query) ||
        circle.description.toLowerCase().includes(query) ||
        circle.createdBy.toLowerCase().includes(query) ||
        (circle.parentCircle &&
          circle.parentCircle.toLowerCase().includes(query))
    );
  }, [circles, searchQuery]);

  // Register page actions with the unified navigation
  const pageActions = useMemo(
    () => ({
      onMainAction: handleCreateCircle,
      onVideoPlay: () =>
        setSelectedVideo({
          videoPath: "/videos/createcircle.webm",
          title: "How to Create a Circle",
        }),
      count: filteredCircles.length,
    }),
    [handleCreateCircle, filteredCircles.length]
  );

  useRegisterPageActions(pageActions);

  // Memoize expensive calculations for performance
  const memoizedStats = useMemo(() => {
    const totalCircles = circles.length;
    const publicCircles = circles.filter((c) => c.isPublic).length;
    return { totalCircles, publicCircles };
  }, [circles]);

  const hasSubCircles = circles.filter((c) => c.isSubCircle).length > 0;
  const subCirclesCount = circles.filter((c) => c.isSubCircle).length;
  const canReorder =
    hasSubCircles && !searchQuery.trim() && subCirclesCount > 1;

  const handleOpenCircle = useCallback(
    (circleId: string) => {
      navigate(`/circles/${circleId}`);
    },
    [navigate]
  );

  const handleReorderCircles = useCallback(
    async (orderedSubcircleIds: string[]) => {
      if (!user?.schoolId || !token) {
        throw new Error("Missing schoolId or token");
      }

      try {
        await api.circles.reorderSubCircles(
          user.schoolId,
          orderedSubcircleIds,
          token
        );
        await loadCircles();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to reorder circles. Please try again.";
        showMessage({ message, severity: "error" });
        throw error;
      }
    },
    [loadCircles, showMessage, token, user?.schoolId]
  );

  const handleCircleUpdated = async (updatedCircle: Circle) => {
    // Refetch circles data before closing to avoid stale reopen state.
    await loadCircles();
    setEditModalOpen(false);
    setCircleToEdit(null);
  };

  const confirmDeleteCircle = async () => {
    if (!circleToDelete || !user || !user.schoolId || !token) return;

    try {
      await api.circles.removeSubCircle(
        circleToDelete.circleId,
        user.schoolId,
        token
      );

      // Refetch circles data to get the latest state
      loadCircles();

      setDeleteModalOpen(false);
      setCircleToDelete(null);
      setDeleteConfirmationText(""); // Clear confirmation text
    } catch (error) {
      console.error("Error deleting circle:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove circle. Please try again.";
      showMessage({ message, severity: "error" });
    }
  };

  if (isLoading) {
    return <CirclesSkeleton />;
  }

  if (error) {
    return (
      <PageContainer>
        <PageSection>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              textAlign: "center",
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 2,
              boxShadow: "none",
            }}
          >
            <Typography variant="h6" color="error.main" sx={{ mb: 2 }}>
              Error Loading Circles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error instanceof Error
                ? error.message
                : "Failed to load circles"}
            </Typography>
            <NoticeboardButton variant="primary" onClick={() => loadCircles()}>
              Try Again
            </NoticeboardButton>
          </Paper>
        </PageSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Stats & Search - only shown when sub-circles exist */}
      {hasSubCircles && (
        <>
          <PageSection>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
                mt: { xs: 2, md: 0 },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 3, md: 5 },
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: styledTheme.colors.primary,
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: "2rem", md: "2.5rem" },
                      }}
                    >
                      {memoizedStats.totalCircles}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Total Circles
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      height: { xs: 50, md: 60 },
                      width: 2,
                      background: `linear-gradient(180deg, transparent 0%, ${styledTheme.colors.primary}40 50%, transparent 100%)`,
                      borderRadius: 1,
                    }}
                  />

                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: styledTheme.colors.primary,
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: "2rem", md: "2.5rem" },
                      }}
                    >
                      {memoizedStats.publicCircles}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Public Circles
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </PageSection>

          <PageSection sx={{ mb: 2 }}>
            <Box sx={{ mb: 3 }}>
              <SearchField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search circles by name, description, or creator..."
                sx={{ width: "100%" }}
              />
            </Box>
          </PageSection>
        </>
      )}

      {/* Circles Grid */}
      <PageSection>
        {circles.length > 0 && (
          <Box>
            {/* Header Row (hide on mobile and when no sub-circles) */}
            {!isMobile && hasSubCircles && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: canReorder
                    ? "auto 1.8fr 1.8fr 1.1fr 1.1fr 1fr"
                    : "1.8fr 1.8fr 1.1fr 1.1fr 1fr",
                  gap: 2,
                  px: 3,
                  py: 1,
                  mb: 1,
                }}
              >
                {canReorder && <Box sx={{ width: 40 }} />}
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
                    letterSpacing: "0.5px",
                  }}
                >
                  Circle Name
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
                    letterSpacing: "0.5px",
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
                    letterSpacing: "0.5px",
                  }}
                >
                  Visibility
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
                    letterSpacing: "0.5px",
                  }}
                >
                  Created
                </Typography>
                <Box></Box>
              </Box>
            )}

            {/* Circle Rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filteredCircles.length > 0 ? (
                <SortableCirclesList
                  circles={filteredCircles}
                  isMobile={isMobile}
                  canReorder={canReorder}
                  onOpen={handleOpenCircle}
                  onReorder={handleReorderCircles}
                />
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No circles found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search terms or create a new circle
                  </Typography>
                </Paper>
              )}
              {filteredCircles.length > 0 && !isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                    pt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCreateCircle}
                    sx={MANAGEMENT_ADD_MORE_BUTTON_SX}
                  >
                    + Add More Circles
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Empty State - shown below circles when no sub-circles exist */}
        {!hasSubCircles && (
          <Box sx={{ mt: 3 }}>
            <EmptyState
              title="Create Your First Sub-Circle in under 30 seconds"
              description="Create circles within your organization to enhance targeted communication."
              imageSrc="/circles/circle.webp"
              imageAlt="Circles illustration"
              buttonText="Add Your First Circle"
              onButtonClick={handleCreateCircle}
              benefits={[
                "Organize your organization into focus areas",
                "Send targeted notices",
                "Reduce message overload",
              ]}
              benefitsTitle="Why create circles?"
              onBenefitsTitleVideoClick={() =>
                setSelectedVideo({
                  videoPath: "/videos/createcircle.webm",
                  title: "How to Create a Circle",
                })
              }
              howItWorks={[
                "Create a sub-circle",
                "Invite members by email or phone",
                "Send notices to select sub-circles",
              ]}
              howItWorksTitle="How it works:"
            />
          </Box>
        )}
      </PageSection>

      {/* Create Circle Modal */}
      <ActionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create New Circle"
        subtitle="Set up a new communication circle for your organization"
        maxWidth="lg"
        headerStyle="light"
        denseContent
        contentSx={{
          px: { xs: 2, sm: 2.5 },
          height: { md: 620 },
        }}
        footerSx={{ px: { xs: 2.5 } }}
        disableBackdropClick={createCircleFooter.isSubmitting}
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              width: "100%",
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Tooltip
              title={
                createCircleFooter.hasReachedLimit
                  ? createCircleFooter.limitMessage
                  : ""
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <NoticeboardButton
                  type="submit"
                  form={CREATE_CIRCLE_FORM_ID}
                  variant="primary"
                  size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
                  loading={createCircleFooter.isSubmitting}
                  disabled={
                    createCircleFooter.isSubmitting ||
                    createCircleFooter.hasReachedLimit
                  }
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    borderRadius: 1,
                    "&.Mui-disabled": {
                      bgcolor: alpha(theme.palette.primary.main, 0.35),
                      color: alpha(theme.palette.common.white, 0.92),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  {`Create Sub-Circle${
                    createCircleFooter.selectedUsersCount > 0
                      ? ` with ${createCircleFooter.selectedUsersCount} Member${
                          createCircleFooter.selectedUsersCount === 1
                            ? ""
                            : "s"
                        }`
                      : ""
                  }`}
                </NoticeboardButton>
              </Box>
            </Tooltip>
          </Box>
        }
      >
        <CreateCircleForm
          anchorSubmitToFooter
          onFooterStateChange={handleCreateCircleFooterState}
          onCircleCreated={() => {
            loadCircles();
            setCreateModalOpen(false);
          }}
          onCancel={() => setCreateModalOpen(false)}
          existingCircles={circles}
          organizationData={{
            schoolLogo: user?.schoolLogo || "/placeholder.jpg",
            type: "school",
          }}
        />
      </ActionModal>

      {/* Edit Circle Modal */}
      <ActionModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Circle"
        subtitle="Update circle information and settings"
        maxWidth="md"
        headerStyle="light"
        denseContent={isMobile}
        contentSx={isMobile ? { px: { xs: 2.5 } } : undefined}
        footerSx={isMobile ? { px: { xs: 2.5 } } : undefined}
        footer={
          isMobile ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                width: "100%",
                justifyContent: "flex-end",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <NoticeboardButton
                type="submit"
                form={EDIT_CIRCLE_FORM_ID}
                variant="primary"
                size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
                loading={editCircleFooter.isSubmitting}
                disabled={editCircleFooter.isSubmitting}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  borderRadius: 2,
                  "&.Mui-disabled": {
                    bgcolor: alpha(theme.palette.primary.main, 0.35),
                    color: alpha(theme.palette.common.white, 0.92),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                Update Circle
              </NoticeboardButton>
            </Box>
          ) : undefined
        }
      >
        {circleToEdit && isMobile ? (
          <EditCircleForm
            key={circleToEdit.circleId}
            circle={circleToEdit}
            anchorSubmitToFooter
            onFooterStateChange={handleEditCircleFooterState}
            onCircleUpdated={handleCircleUpdated}
            onCancel={() => setEditModalOpen(false)}
          />
        ) : (
          circleToEdit && (
            <Box sx={{ p: 3 }}>
              <EditCircleForm
                key={circleToEdit.circleId}
                circle={circleToEdit}
                onCircleUpdated={handleCircleUpdated}
                onCancel={() => setEditModalOpen(false)}
              />
            </Box>
          )
        )}
      </ActionModal>

      {/* Delete Circle Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteConfirmationText(""); // Clear confirmation text
        }}
        title="Confirm Removal"
        subtitle="This action cannot be undone"
      >
        <Typography
          variant="body1"
          sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
        >
          Are you sure you want to remove the circle{" "}
          <Typography component="span" fontWeight={600}>
            "{circleToDelete?.name}"
          </Typography>
          ? This action cannot be undone and all circle data will be permanently
          removed.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To confirm removal, please type the circle name:{" "}
            <Typography component="span" fontWeight={600} color="error.main">
              {circleToDelete?.name}
            </Typography>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type the circle name to confirm"
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="danger"
            onClick={confirmDeleteCircle}
            disabled={deleteConfirmationText !== circleToDelete?.name}
          >
            Remove Circle
          </NoticeboardButton>
        </Box>
      </DeleteModal>

      {/* Manage Circle Users Modal */}
      {circleForManagingUsers && (
        <ManageCircleUsers
          open={manageUsersModalOpen}
          onClose={() => setManageUsersModalOpen(false)}
          subcircleId={circleForManagingUsers.circleId}
          subcircleName={circleForManagingUsers.name}
          onUsersChanged={() => {
            // Refresh circles data after users are added/removed
            loadCircles();
          }}
        />
      )}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="Create circle"
          onClick={handleCreateCircle}
          sx={mobileFabSx}
        >
          <AddIcon sx={{ fontSize: 30 }} />
        </Fab>
      )}
      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </PageContainer>
  );
};

export default Circles;
